import { Components, Theme } from '@mui/material/styles';

/**
 * Portal Pixels Button Component Theming
 *
 * Based exactly on Portal Pixels Figma design system:
 * https://www.figma.com/design/RcTf0wSf31Elw7uY8VL6rH/Portal-Pixels?node-id=1-11139&p=f&m=dev
 *
 * Only overrides styles that differ from themeDefault.tsx
 * Rest will be inherited from base theme
 */

type ButtonColor = 'primary' | 'secondary' | 'success' | 'warning' | 'error';
type ButtonVariant = 'contained' | 'outlined' | 'text';

/**
 * Get color palette for a specific button color
 */
const getColorPalette = (theme: Theme, color: ButtonColor) => {
  const colorMap = {
    primary: theme.palette.primary,
    secondary: theme.palette.secondary,
    success: theme.palette.success,
    warning: theme.palette.warning,
    error: theme.palette.error,
  };
  return colorMap[color];
};

/**
 * Create focus-visible styles
 */
const createFocusStyles = (color: string) => ({
  '&:focus-visible': {
    outline: `2px solid ${color}`,
    outlineOffset: 2,
  },
});

/**
 * Create contained button variant styles
 */
const createContainedButtonStyles = (theme: Theme, color: ButtonColor) => {
  const palette = getColorPalette(theme, color);

  return {
    background: palette.main,
    color: theme.palette.common.white,
    '&:hover': {
      background: palette.dark,
    },
    '&:active': {
      background: palette.dark,
    },
    ...createFocusStyles(palette.main),
    '&:disabled': {
      background: theme.palette.text.buttonDisabled,
      color: theme.palette.text.buttonTextDisabled,
      '& .MuiLoadingButton-loadingIndicator': {
        color: theme.palette.grey.A200,
      },
    },
  };
};

/**
 * Create outlined button variant styles
 */
const createOutlinedButtonStyles = (theme: Theme, color: ButtonColor) => {
  const palette = getColorPalette(theme, color);

  return {
    background: 'transparent',
    borderColor: palette.main,
    color: palette.main,
    '&:hover': {
      borderColor: 'inherit',
    },
    '&:active': {
      borderColor: palette.main,
    },
    ...createFocusStyles(palette.main),
    '&:disabled': {
      borderColor: theme.palette.border.dark,
      background:
        color === 'secondary' ? theme.palette.common.white : 'transparent',
      color: theme.palette.text.buttonTextDisabled,
    },
  };
};

/**
 * Create text button variant styles
 */
const createTextButtonStyles = (theme: Theme, color: ButtonColor) => {
  const palette = getColorPalette(theme, color);

  return {
    padding: 0,
    minWidth: 0,
    background: 'transparent',
    color: theme.palette.secondary.main,
    '&:hover': {
      color: theme.palette.secondary.dark,
    },
    '&:active': {
      color: theme.palette.secondary.dark,
    },
    ...createFocusStyles(palette.main),
    '&:disabled': {
      color: theme.palette.text.buttonTextDisabled,
      background: 'transparent',
    },
  };
};

/**
 * Generate button variant configuration
 */
const createButtonVariant = (
  variant: ButtonVariant,
  color: ButtonColor,
  theme: Theme,
) => {
  const styleCreators = {
    contained: createContainedButtonStyles,
    outlined: createOutlinedButtonStyles,
    text: createTextButtonStyles,
  };

  return {
    props: { variant, color },
    style: styleCreators[variant](theme, color),
  };
};

/**
 * Generate all button variants for all colors
 */
const generateButtonVariants = (theme: Theme) => {
  const variants: ButtonVariant[] = ['contained', 'outlined', 'text'];
  const colors: ButtonColor[] = [
    'primary',
    'secondary',
    'success',
    'warning',
    'error',
  ];

  const buttonVariants = [];

  for (const variant of variants) {
    for (const color of colors) {
      buttonVariants.push(createButtonVariant(variant, color, theme));
    }
  }

  return buttonVariants;
};

export const createButtonTheme = (theme: Theme): Components['MuiButton'] => {
  return {
    defaultProps: {
      size: 'small',
    },
    styleOverrides: {
      root: {
        borderRadius: 6,
        textTransform: 'capitalize',
        boxShadow: 'none',
        '&:not(.MuiButton-text)': {
          minWidth: 128,
        },
        '&:hover': {
          boxShadow: 'none',
        },
      },

      sizeSmall: {
        '&:not(.MuiButton-text)': {
          height: 40,
        },
        padding: theme.spacing(1.25, 2),
        fontSize: 14,
      },

      sizeMedium: {
        '&:not(.MuiButton-text)': {
          height: 48,
        },
        padding: theme.spacing(1.25, 2),
        fontSize: 16,
      },

      sizeLarge: {
        '&:not(.MuiButton-text)': {
          height: 64,
        },
        padding: theme.spacing(1.25, 4),
        fontSize: 16,
      },
    },

    variants: generateButtonVariants(theme),
  };
};

/**
 * Create Link Component Theme
 */
export const createLinkTheme = (theme: Theme): Components['MuiLink'] => {
  return {
    styleOverrides: {
      root: {
        color: theme.palette.secondary.main,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '20px',
        '&:hover': {
          color: theme.palette.secondary.dark,
        },
        // Style CTB links - target Typography children
        '&[data-ctb-link="true"] .MuiTypography-root': {
          fontWeight: 500,
          textTransform: 'capitalize',
        },
        // Style renew links - target Typography children
        '&[data-renew-link="true"] .MuiTypography-root': {
          fontWeight: 500,
          textTransform: 'capitalize',
        },
      },
    },
  };
};

export default createLinkTheme;
